// ---------------------------------------------------------------------------
// V8's gate, as the console understands it.
//
// THE MOST IMPORTANT THING IN THIS FILE IS WHAT IT DOES NOT DO.
//
// It does not compare a date. It does not know that a credential expiring on the
// 14th is unusable for a shift ending on the 20th. 09-... §V8.2 is precisely the
// case a second implementation gets wrong, and there are now three places that
// could hold one — the trigger, the API and here. Two of those already share a
// single Postgres function; this file makes the third a renderer.
//
// So `gateTone` maps a verdict the SERVER produced onto a tone, and the sentence
// shown to a person is the server's `explanation` verbatim. If the rule changes,
// it changes in one function in one migration, and this file keeps working
// without being edited — which is the only version of "they cannot drift apart"
// that survives contact with a later slice.
//
// NO VOCABULARY IS WRITTEN HERE EITHER. The statuses below are the gate
// function's own return values, not a credential taxonomy: `missing`,
// `unverified`, `expires_before` and `satisfied` are states of the CHECK, and
// they are the same four whatever the credential is. A vertical added at V9 adds
// no case to this switch.
//
// THE SENTENCE ABOVE ORIGINALLY LISTED THREE EXAMPLE CREDENTIALS, and the
// taxonomy guard failed on it — the fourth time a comment EXPLAINING why no
// vocabulary may be named has itself named one. The rule has no prose exemption
// and must not acquire one: api/src and web/src name no vocabulary at all,
// including in comments, because a per-file exemption is how a guard stops
// guarding.
// ---------------------------------------------------------------------------

import { toneOf, type Tone } from "../design/tone";
import { english, type Translate } from "../i18n/translate";

export type GateVerdict = {
  credential_type_id: string;
  credential_name: string;
  is_statutory: boolean;
  /** The gate function's own state. Not a credential vocabulary, and never compared here. */
  status: string;
  /** S70 (0203). The state's words and tone from `app.credential_gate_status`; null
   *  when the vocabulary has no row for it. */
  status_label: string | null;
  status_tone: string | null;
  /** S70 (0203). Whether this state means the credential is held. */
  held: boolean | null;
  expires_on: string | null;
  overridden: boolean;
  override_reason: string | null;
  blocks: boolean;
  /** Composed by the database. Rendered verbatim; never reconstructed here. */
  explanation: string;
};

/**
 * V15. One conflict on a prospective assignment, from any of the four classes.
 *
 * `kind` and `severity` are ids from vocabularies the DATABASE owns, so nothing
 * here holds a list of them. That matters more than it looks: `travel_time` is in
 * that table with `is_detectable = false`, so the day somebody gives this product
 * venue coordinates, the class appears without a line of TypeScript changing.
 */
export type Conflict = {
  kind: string;
  severity: string;
  /** Resolved from the organisation's own rule. §V15's "blocking or warning per config". */
  refuses: boolean;
  /** Composed by the database. Rendered verbatim; never reconstructed here. */
  explanation: string;
  /** S70 (0203). This kind is the credential gate, already listed verdict by verdict. */
  repeats_the_gate?: boolean;
  /** S0-176 (0317). Counsel's kind for a working-time check, worded by the database; null otherwise. */
  check_kind?: string | null;
  check_kind_label?: string | null;
};

export type GateResult = {
  requirements: GateVerdict[];
  /**
   * V8'S FIELD, AND IT STILL MEANS ONLY THE CREDENTIALS. Kept at its original
   * meaning rather than widened, because verify-V8.sh and this console both read
   * it. `refused` is V15's broader answer.
   */
  blocked: boolean;

  /** V15. Every class, credential gap included — a superset of `requirements`. */
  conflicts: Conflict[];
  /** V15. Whether anything at all refuses. The question a scheduler is asking. */
  refused: boolean;
  /** V15. The conflicts that do NOT refuse, so a warning reaches somebody. */
  warnings: Conflict[];
};

/**
 * The chip a verdict wears.
 *
 * SEMANTIC DISCIPLINE, from the brief: teal = live and confirmed, amber =
 * attention, red = failed, clay-brown = action and never status. A waived
 * requirement is AMBER and not teal, and that is the one judgement in this file
 * worth arguing: the credential is still not held. Rendering it teal would say
 * the licence was fine, which is the quiet lie a Proof Pack must never tell —
 * and the person reading the board is the one who has to decide whether the
 * waiver is still a good idea on the day.
 */
export function gateTone(verdict: GateVerdict): { tone: Tone; label: string } {
  // S70. The words and tone are the status row's (0203); this file compares no status.
  if (!verdict.held && verdict.overridden) return { tone: "attention", label: "Waived" };
  // An unrecognised state is a FAILURE, not a shrug. A new state added to the
  // gate function with no row, that this console silently rendered as neutral,
  // would be a block nobody could see.
  if (verdict.status_label === null || verdict.status_tone === null) return { tone: "failed", label: "Not satisfied" };
  return { tone: toneOf(verdict.status_tone), label: verdict.status_label };
}

/**
 * A one-line summary for a shift, for the places a list has no room for five
 * sentences. Never invents a reason: it counts, and defers to the server's
 * sentence when there is exactly one thing wrong.
 */
export function gateSummary(result: GateResult, t: Translate = english): string {
  const blocking = result.requirements.filter((r) => r.blocks);
  if (blocking.length === 0) {
    return result.requirements.length === 0
      ? t("This role requires no credentials.")
      : t("Every credential this role requires is in place for this shift.");
  }
  if (blocking.length === 1) return blocking[0]!.explanation;
  return t("{n} credentials this role requires are not satisfied for this shift.", { n: blocking.length });
}

/**
 * V15. The conflicts that are NOT about credentials, as one sentence.
 *
 * WHY IT EXCLUDES THE CREDENTIAL ROWS. They are already rendered, verdict by
 * verdict, by everything that reads `requirements` — the gate's own list has a
 * chip and an expiry date per credential, which is more than a sentence can say.
 * Repeating them here would show a scheduler the same refusal twice and leave
 * them wondering whether it was two problems.
 *
 * It counts and defers to the server's sentence when there is exactly one thing
 * wrong, exactly as `gateSummary` does — because the alternative is this file
 * inventing a reason, and the database is the only place that knows what the
 * organisation's own conflict rules say.
 */
export function conflictSummary(result: GateResult, t: Translate = english): string | null {
  const other = result.conflicts.filter((c) => !c.repeats_the_gate);
  if (other.length === 0) return null;
  if (other.length === 1) return other[0]!.explanation;

  const refusing = other.filter((c) => c.refuses).length;
  if (refusing === 0) return t("{n} things about this shift are worth checking.", { n: other.length });
  if (refusing === other.length) return t("{n} things stop this shift being booked.", { n: refusing });
  return t("{refusing} of {n} things about this shift stop it being booked.", { refusing, n: other.length });
}

/**
 * The tone a conflict wears. A WARNING IS AMBER AND NOT NEUTRAL, for the same
 * reason a waived credential is amber and not teal: the thing is still true, the
 * organisation has simply decided not to refuse over it, and rendering that as
 * nothing would make the configuration a way of hiding problems rather than a way
 * of choosing which ones stop the work.
 */
export function conflictTone(conflict: Conflict): { tone: Tone; label: string } {
  return conflict.refuses ? { tone: "failed", label: "Blocks" } : { tone: "attention", label: "Warning" };
}
